import React, {useEffect, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {Spinner} from 'components/ui-kit/Spinner';
import {DropOutReasons} from 'core/dictionaries';
import {
    isRecallModalOpen$,
    isRecallModalOpenChanged,
    recallGtinFx,
    uploadState$,
} from 'models/registries/gtinsGrid/upload';

import {Buttons, Description, Form, SpinnerLayer} from './styled';

export const GtinsRecallForm = () => {
    const [steps, setSteps] = useState<number>(0);
    const {isRecallPending, isRecallFailed} = useStore(uploadState$);
    const isModalOpen = useStore(isRecallModalOpen$);
    const {t} = useTranslation();
    const {
        control,
        trigger,
        handleSubmit,
        getValues,
        reset,
        formState: {errors},
    } = useForm<{reason: string; gtin: string}>({
        defaultValues: {
            reason: '',
            gtin: '',
        },
        shouldUnregister: false,
        mode: 'all',
        resolver: yupResolver(
            yup.object().shape({
                reason: yup.string().required(),
                gtin: yup.number().required(),
            }),
        ),
    });
    const onSubmit = (data: {reason: string; gtin: string}) => {
        recallGtinFx({gtin: data.gtin, reason: data.reason});
    };

    const onClose = () => {
        isRecallModalOpenChanged(false);
        setSteps(0);
    };

    const handleNextStep = async () => {
        const isValid = await trigger(['reason', 'gtin']);
        if (isValid) {
            setSteps(1);
        }
    };

    const handleFinalSubmit = async () => {
        const values = getValues();
        onSubmit({
            reason: values.reason,
            gtin: values.gtin,
        });
    };

    const ModalSpinner = () => (
        <SpinnerLayer style={{minHeight: 254}}>
            <Spinner />
        </SpinnerLayer>
    );

    useEffect(() => {
        if (isModalOpen || isRecallFailed) {
            setSteps(0);
            reset();
        }
    }, [isModalOpen, isRecallFailed]);

    const REASONS = Object.keys(DropOutReasons).map((value: string) => ({
        label: t('dictionaries.documents.DropOut.reasons.' + value),
        value,
    }));

    return (
        <Modal
            modalWidth={0}
            show={isModalOpen}
            onClose={() => {
                onClose();
            }}
            title={
                steps === 0
                    ? t('pages.registries.recallForm.title')
                    : t('pages.registries.recallForm.title2')
            }
        >
            {isRecallPending ? (
                <ModalSpinner />
            ) : (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    {steps === 0 ? (
                        <>
                            <Description step={steps}>
                                {t('pages.registries.recallForm.description')}
                            </Description>
                            <Controller
                                control={control}
                                name="reason"
                                render={(props) => (
                                    <Select
                                        onChange={(val: SelectOption<string>) => {
                                            props.onChange(val.value);
                                        }}
                                        value={
                                            REASONS.filter(
                                                (el: SelectOption<string>) =>
                                                    el.value === props.value,
                                            )[0]
                                        }
                                        label={t('pages.documents.form.reason')}
                                        hasError={!!errors.reason}
                                        options={REASONS}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={`gtin`}
                                render={(props) => (
                                    <Input
                                        {...props}
                                        label={t('pages.registries.recallForm.gtin')}
                                        placeholder={t(
                                            'pages.registries.recallForm.gtinPlaceholder',
                                        )}
                                        hasError={!!errors.gtin}
                                    />
                                )}
                            />
                        </>
                    ) : (
                        <Description step={steps}>
                            {t('pages.registries.recallForm.description2')}
                        </Description>
                    )}
                    <Buttons>
                        <Button
                            type="button"
                            variant={ButtonVariant.Outline}
                            onClick={() => {
                                onClose();
                            }}
                        >
                            {t('pages.registries.recallForm.cancel')}
                        </Button>
                        <Button
                            type="button"
                            disabled={isRecallPending}
                            variant={ButtonVariant.Normal}
                            onClick={() => {
                                if (steps === 0) {
                                    handleNextStep();
                                } else {
                                    handleFinalSubmit();
                                }
                            }}
                        >
                            {t('pages.registries.recallForm.submit')}
                        </Button>
                    </Buttons>
                </Form>
            )}
        </Modal>
    );
};
